import React, { useCallback, useEffect, useState } from 'react';
import { BasecampLayout } from '../../components/Layout';
import { PageHeader, Spinner, Alert, Btn, Input, Select, SectionCard, Card } from '../../components/UI';
import { api, getToken, BASE } from '../../utils/api';

// HB Sales Tool W7 — AI usage and cost per person, partner and function (metadata only: no prompt or
// answer text). Administration sets the monthly limits; the alert shows at the limit's percentage and at 90 %.

const th = { textAlign: 'left', padding: '8px 10px', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)', borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap' };
const td = { padding: '9px 10px', borderBottom: '1px solid var(--line)', fontSize: '0.86rem' };
const num = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const eur = (v) => `€${(Number(v) || 0).toFixed(2)}`;
const tok = (v) => (Number(v) || 0).toLocaleString('en-GB');
const thisMonth = () => new Date().toISOString().slice(0, 7);
const FUNC = { sync: 'Sync', needs: 'Needs', design: 'Design', proposal: 'Proposal', translation: 'Translation', 'translation-shelf': 'Translation (shelf)', assistant: 'Assistant', contribution: 'Contribution' };

function AlertCell({ x }) {
  if (x.limit == null) return <td style={{ ...td, color: 'var(--muted)' }}>no limit</td>;
  const red = x.alert === 'over' || x.alert >= 90;
  return <td style={{ ...td, color: red ? 'var(--gate)' : x.alert ? 'var(--ink)' : 'var(--muted)', fontWeight: x.alert ? 700 : 400 }}>{x.pct} % of {eur(x.limit)}{x.alert === 'over' ? ' · over' : ''}</td>;
}

export default function AIUsage() {
  const [month, setMonth] = useState(thisMonth());
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [lim, setLim] = useState({ scope: 'person', key: '', monthlyEur: '', alertPct: 75 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { setError(null); api.usage.month(month).then(setD).catch(e => setError(e.message)); }, [month]);
  useEffect(() => { load(); }, [load]);

  async function exportCsv() {
    const res = await fetch(`${BASE}/api/basecamp/usage.csv?month=${month}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) { setError('The export failed.'); return; }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(await res.blob());
    a.download = `ai-usage-${month}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  async function saveLimit() {
    setBusy(true); setError(null);
    try { await api.usage.setLimit({ ...lim, monthlyEur: Number(lim.monthlyEur), alertPct: Number(lim.alertPct) }); setLim(l => ({ ...l, key: '', monthlyEur: '' })); load(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  async function removeLimit(l) {
    try { await api.usage.removeLimit(l.scope, l.key); load(); } catch (e) { setError(e.message); }
  }

  return (
    <BasecampLayout>
      <PageHeader title="AI usage & cost" subtitle="Per person, partner and function — tokens and cost in EUR, metadata only" />
      {error && <Alert type="error">{error}</Alert>}
      <Card style={{ padding: '12px 18px', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700 }}>Month</span>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value || thisMonth())} style={{ width: 'auto' }} />
          {d && <span style={{ fontSize: '0.86rem', color: 'var(--body-ink)' }}>{d.calls} calls · <b>{eur(d.totalEur)}</b></span>}
          <Btn size="sm" variant="outline" onClick={exportCsv} style={{ marginLeft: 'auto' }} disabled={!d}>Export CSV</Btn>
        </div>
      </Card>

      {!d ? <Spinner /> : (
        <>
          <SectionCard title="Per person">
            <Table head={['Person', 'Calls', 'Input tokens', 'Output tokens', 'Charged to the person', 'Charged to the network', 'Total', 'Limit']}
              rows={d.byPerson} empty="No AI use this month." render={x => (
                <tr key={x.userId || 0}>
                  <td style={td}><b>{x.name}</b></td><td style={num}>{x.calls}</td><td style={num}>{tok(x.inputTokens)}</td><td style={num}>{tok(x.outputTokens)}</td>
                  <td style={num}>{eur(x.user)}</td><td style={num}>{eur(x.network)}</td><td style={{ ...num, fontWeight: 700 }}>{eur(x.costEur)}</td><AlertCell x={x} />
                </tr>
              )} />
          </SectionCard>

          <SectionCard title="Per partner (territory)">
            <Table head={['Partner', 'Calls', 'Input tokens', 'Output tokens', 'Charged to people', 'Charged to the network', 'Total', 'Limit']}
              rows={d.byPartner} empty="No AI use this month." render={x => (
                <tr key={x.territory}>
                  <td style={td}><b>{x.territory === '—' ? 'no territory' : x.territory}</b></td><td style={num}>{x.calls}</td><td style={num}>{tok(x.inputTokens)}</td><td style={num}>{tok(x.outputTokens)}</td>
                  <td style={num}>{eur(x.user)}</td><td style={num}>{eur(x.network)}</td><td style={{ ...num, fontWeight: 700 }}>{eur(x.costEur)}</td><AlertCell x={x} />
                </tr>
              )} />
          </SectionCard>

          <SectionCard title="Per function">
            <Table head={['Function', 'Calls', 'Input tokens', 'Output tokens', 'Total']} rows={d.byFunc} empty="No AI use this month." render={x => (
              <tr key={x.func}>
                <td style={td}><b>{FUNC[x.func] || x.func}</b></td><td style={num}>{x.calls}</td><td style={num}>{tok(x.inputTokens)}</td><td style={num}>{tok(x.outputTokens)}</td><td style={{ ...num, fontWeight: 700 }}>{eur(x.costEur)}</td>
              </tr>
            )} />
          </SectionCard>

          <SectionCard title="Monthly limits" hint="The person or partner sees an alert at the limit's percentage (75 % unless set) and again at 90 %.">
            <Table head={['Who', 'Scope', 'Monthly limit', 'Alert at', '']} rows={d.limits} empty="No limits yet." render={l => (
              <tr key={`${l.scope}-${l.key}`}>
                <td style={td}><b>{l.label}</b></td><td style={td}>{l.scope === 'bp' ? 'partner' : 'person'}</td><td style={num}>{eur(l.monthlyEur)}</td><td style={num}>{l.alertPct} %</td>
                <td style={{ ...td, textAlign: 'right' }}>{d.canEdit && <Btn size="sm" variant="outline" onClick={() => removeLimit(l)}>Remove</Btn>}</td>
              </tr>
            )} />
            {d.canEdit && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
                <Select value={lim.scope} onChange={e => setLim(l => ({ ...l, scope: e.target.value, key: '' }))} style={{ width: 'auto' }}>
                  <option value="person">Person</option><option value="bp">Partner (territory)</option>
                </Select>
                {lim.scope === 'person' ? (
                  <Select value={lim.key} onChange={e => setLim(l => ({ ...l, key: e.target.value }))} style={{ width: 'auto', minWidth: 200 }}>
                    <option value="">Choose a person…</option>{d.users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </Select>
                ) : (
                  <Input value={lim.key} onChange={e => setLim(l => ({ ...l, key: e.target.value }))} placeholder="Territory" list="ai-territories" style={{ width: 200 }} />
                )}
                <datalist id="ai-territories">{d.territories.map(t => <option key={t} value={t} />)}</datalist>
                <Input type="number" min="1" step="1" value={lim.monthlyEur} onChange={e => setLim(l => ({ ...l, monthlyEur: e.target.value }))} placeholder="EUR / month" style={{ width: 130 }} />
                <Input type="number" min="10" max="99" value={lim.alertPct} onChange={e => setLim(l => ({ ...l, alertPct: e.target.value }))} style={{ width: 80 }} />
                <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>% alert</span>
                <Btn size="sm" loading={busy} disabled={!lim.key || !(Number(lim.monthlyEur) > 0)} onClick={saveLimit}>Set limit</Btn>
              </div>
            )}
          </SectionCard>
        </>
      )}
    </BasecampLayout>
  );
}

function Table({ head, rows, render, empty }) {
  if (!rows.length) return <p style={{ fontSize: '0.86rem', color: 'var(--muted)', margin: 0 }}>{empty}</p>;
  return (
    <div style={{ overflow: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead><tr>{head.map((h, i) => <th key={i} style={{ ...th, textAlign: i === 0 || h === 'Scope' || h === '' ? 'left' : 'right' }}>{h}</th>)}</tr></thead>
        <tbody>{rows.map(render)}</tbody>
      </table>
    </div>
  );
}
